/**
 * Lightweight language helper.
 *
 * Language is detected from the hostname:
 *   app.springtime.no        -> Norwegian
 *   app.springtime.dk        -> Danish
 *   app.springtime.fi        -> Finnish
 *   app.springtimetravel.com -> English
 *   anything else            -> Swedish (default)
 *
 * This is the same signal InstallPrompt has always used — centralised here so
 * every component agrees on which language to render. One bundle serves every
 * domain; nothing is decided at build time.
 *
 * Note: the Nordic sites are matched on TLD, but English lives on a separate
 * base domain (springtimetravel.com), so it must match the full hostname —
 * matching ".com" alone would catch unrelated domains.
 */

export type Lang = 'sv' | 'no' | 'da' | 'fi' | 'en';

const hostname = (): string =>
  typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';

export const lang = (): Lang => {
  const host = hostname();
  if (host.endsWith('springtimetravel.com')) return 'en';
  if (host.endsWith('.no')) return 'no';
  if (host.endsWith('.dk')) return 'da';
  if (host.endsWith('.fi')) return 'fi';
  return 'sv';
};

export const isNorwegian = (): boolean => lang() === 'no';

/** BCP 47 tag for <html lang> and Intl APIs. */
export const HTML_LANG: Record<Lang, string> = {
  sv: 'sv-SE',
  no: 'nb-NO',
  da: 'da-DK',
  fi: 'fi-FI',
  en: 'en-GB',
};

/** Pick the value for the current language. */
export const t = <T>(translations: Record<Lang, T>): T => translations[lang()];

// Full weekday names (used by ScheduleOverview)
export const DAY_NAMES: Record<Lang, string[]> = {
  sv: ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'],
  no: ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
  da: ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
  fi: ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
};

// Abbreviated weekday names (used by the Schedule day picker)
export const DAY_NAMES_SHORT: Record<Lang, string[]> = {
  sv: ['Sön', 'Mån', 'Tis', 'Ons', 'Tors', 'Fre', 'Lör'],
  no: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
  da: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
  fi: ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};

// Month names (used by ScheduleOverview)
export const MONTHS: Record<Lang, string[]> = {
  sv: ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'],
  no: ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'],
  da: ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'],
  fi: ['tammikuuta', 'helmikuuta', 'maaliskuuta', 'huhtikuuta', 'toukokuuta', 'kesäkuuta', 'heinäkuuta', 'elokuuta', 'syyskuuta', 'lokakuuta', 'marraskuuta', 'joulukuuta'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

/**
 * Meta description, shown by Google and in link previews (Slack, iMessage, …).
 *
 * Caveat: crawlers that do not run JavaScript only ever see the Swedish copy
 * baked into public/index.html — this only helps clients that execute JS.
 * A proper fix needs prerendering or per-domain index.html files at deploy time.
 */
export const META_DESCRIPTION: Record<Lang, string> = {
  sv: 'Springtime Travel arrangerar träningsresor! Med ett brett utbud såsom löparresor och Training Camp kan ni åka med oss på träningsresor. Välkommen att upptäcka våra resor!',
  no: 'Springtime Travel arrangerer treningsreiser! Med et bredt utvalg som løpereiser og Training Camp kan du reise med oss på treningsreiser. Velkommen til å oppdage våre reiser!',
  da: 'Springtime Travel arrangerer træningsrejser! Med et bredt udvalg såsom løberejser og Training Camp kan du rejse med os på træningsrejser. Velkommen til at opdage vores rejser!',
  fi: 'Springtime Travel järjestää treenimatkoja! Laajasta valikoimastamme löydät muun muassa juoksumatkoja ja Training Camp -matkoja. Tule mukaan ja tutustu matkoihimme!',
  en: 'Springtime Travel organises training trips! With a wide range of running trips and Training Camps, you can join us on a training holiday. Come and explore our trips!',
};
